import React, { useContext } from 'react';
import styled from 'styled-components';
import Tooltip from 'components/controls/Tooltip';
import FontIcon from 'components/icons/FontIcon';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import useHeaderAccessToCompanySettings from './useHeaderAccessToCompanySettings';
import { media } from './components/headerHelpers';

const HeaderCompanyInfoMain: React.FC = () => {
  const { companyId, company } = useContext(CompanyContext);
  const companyAccess = useHeaderAccessToCompanySettings();

  const { t } = useTranslation();

  if (companyAccess.status === 'pending') {
    return <Wrapper>{t('messages.loadingOrganisation')}</Wrapper>;
  }

  if (companyAccess.status === 'error') {
    return <Wrapper>Error</Wrapper>;
  }

  const companyNameText = company?.name || '[Company name is empty]';

  const companyName = (
    <CompanyName>
      <EllipsedName>
        {companyNameText}
        <Tooltip position="titleEllipsed">{companyNameText}</Tooltip>
      </EllipsedName>
    </CompanyName>
  );

  return (
    <Wrapper>
      {companyAccess.hasAccess ? (
        <StyledLink to={EpdLinks.manageCompany(companyId!)}>{companyName}</StyledLink>
      ) : (
        companyName
      )}

      {company?.parentCompany && (
        <HoldingTooltipHost>
          <HoldingIcon icon="holding-company" />
          <HoldingTooltip>
            <HoldingName>{company.parentCompany.name}</HoldingName>
            <HoldingSubtitle>Holding company</HoldingSubtitle>
          </HoldingTooltip>
        </HoldingTooltipHost>
      )}
    </Wrapper>
  );
};

export default HeaderCompanyInfoMain;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  @media ${media.LESS_THAN_DESKTOP} {
    display: none;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const CompanyName = styled.div`
  color: ${(props) => props.theme.colors.primaryBlack};
  line-height: 20px;
  font-weight: 600;
`;

const HoldingTooltipHost = styled.span`
  position: relative;
`;

const HoldingIcon = styled(FontIcon)`
  display: block;
  transform: translateY(1px);
`;

const HoldingTooltip = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  min-width: 184px;
  max-width: 35rem;
  background-color: rgba(21, 21, 21, 0.7);
  color: #ffffff;
  border-radius: 4px;
  padding: 19px 24px;

  display: none;
  ${HoldingIcon}:hover ~ & {
    display: block;
  }
`;

const HoldingName = styled.div`
  font-weight: 600;
  font-size: 16px;
`;

const HoldingSubtitle = styled.div`
  margin-top: 8px;
  font-weight: 400;
  font-size: 16px;
  opacity: 0.7;
`;

const EllipsedName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;

  ${(props) => props.theme.media.largeDesktop} {
    max-width: 300px;
  }
`;
